import { useMemo } from 'react'
import { ArrowLeft, DollarSign, ArrowRight } from 'lucide-react'
import { useMatch } from '../hooks/useMatches'
import { useMatchGames } from '../hooks/useGames'
import { useSideBets } from '../hooks/useSideBets'
import { useMoneyByPlayer } from '../hooks/useMoneyByPlayer'
import { computeSettleUpTransactions } from '../utils/settleUp'
import { getInitials } from '../utils/colors'

/**
 * Settle Up page — shows the minimum set of transactions to square up all debts.
 * Read-only display, no persistence.
 */
export default function SettleUp({ matchId, onBack }) {
  const { data: match } = useMatch(matchId)
  const { data: matchGames } = useMatchGames(matchId)
  const { data: sideBets } = useSideBets(matchId)

  const moneyByPlayer = useMoneyByPlayer({ match, matchGames, sideBets })

  const transactions = useMemo(
    () => computeSettleUpTransactions(moneyByPlayer),
    [moneyByPlayer]
  )

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1rem',
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            padding: '0.25rem',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Settle Up</h1>
      </div>

      {transactions.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40vh',
          gap: '1rem',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}>
          <DollarSign size={48} />
          <p style={{ fontSize: '0.875rem' }}>All square — no payments needed!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {transactions.map((t, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.875rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}
            >
              {/* Payer avatar */}
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: t.payer?.avatar_color || 'var(--bg-tertiary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '0.75rem',
                color: '#fff',
                flexShrink: 0,
              }}>
                {getInitials(t.payer)}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '500', fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--accent-danger)' }}>{t.payer?.display_name}</span>
                  <ArrowRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--accent-primary)' }}>{t.payee?.display_name}</span>
                </div>
              </div>

              <span style={{
                fontWeight: '700',
                fontSize: '1.125rem',
                color: 'var(--accent-warning)',
                flexShrink: 0,
              }}>
                ${t.amount}
              </span>
            </div>
          ))}

          <p style={{
            textAlign: 'center',
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
            marginTop: '0.5rem',
          }}>
            {transactions.length} payment{transactions.length !== 1 ? 's' : ''} to settle all debts
          </p>
        </div>
      )}
    </div>
  )
}
